// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/SourcesPanel.test.tsx
import { DotsThreeOutlineVertical, UploadSimple } from "@phosphor-icons/react";
import {
  AlertDialog,
  Badge,
  Button,
  Dialog,
  DropdownMenu,
  Flex,
  IconButton,
  Link as RadixLink,
  Table,
  Text,
} from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ScopeChips, ScopeEditPopover } from "./ScopeEditPopover";

import { FileDropzone } from "../../../../../components/FileDropzone";
import { useUI } from "../../../../../contexts/ui";
import { canManageKnowledgeDatabase } from "../../../../../utils/auth";
import { errorMessage } from "../../../../../utils/http";
import {
  deriveSourceStatus,
  KnowledgeTrustLevel,
  type DerivedSourceStatus,
  type KnowledgeSourceResult,
} from "../../../../../utils/parsers/knowledge";
import {
  useDeleteSourceMutation,
  useKnowledgeSourcesQuery,
  useRetryExtractionMutation,
  useUpdateSourceScopeMutation,
  useUploadKnowledgeSourceMutation,
} from "../../../../../utils/queries/knowledge";
import { knowledgeDatabaseRoute } from "../route";

const ACCEPTED_TYPES = ".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md";
const MAX_SIZE_BYTES = 100 * 1024 * 1024;

function statusColor(
  status: DerivedSourceStatus,
): "gray" | "blue" | "amber" | "green" | "red" {
  switch (status.kind) {
    case "ready":
      return "green";
    case "extracting":
      return "amber";
    case "failed":
    case "extraction_failed":
      return "red";
    case "uploaded":
    case "processing":
    default:
      return "blue";
  }
}

function UploadSourceDialog({
  open,
  onOpenChange,
  onUpload,
  isUploading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpload: (file: File) => void;
  isUploading: boolean;
}) {
  const { t } = useTranslation("ui");
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        aria-label={t("settings.sourcesPanel.uploadGuidelineDocument")}
        data-testid="knowledge-sources-upload-dialog"
      >
        <Dialog.Title>
          {t("settings.sourcesPanel.uploadGuidelineDocument")}
        </Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Puntt extracts rules from your guidelines automatically — every rule
          links back to the exact page or cell it came from.
        </Dialog.Description>
        <Flex direction="column" gap="3" mt="3">
          <FileDropzone
            onFiles={(files) => {
              const file = files[0];
              if (file) onUpload(file);
            }}
            accept={ACCEPTED_TYPES}
            maxSizeBytes={MAX_SIZE_BYTES}
            disabled={isUploading}
            ariaLabel={t("settings.sourcesPanel.uploadGuidelineDocument")}
            helperText={t("settings.sourcesPanel.pdfWordExcelCsvText")}
            data-testid="knowledge-sources-dropzone"
            inputTestId="knowledge-sources-dropzone-input"
          />
          {isUploading ? (
            <Text
              size="1"
              color="gray"
              data-testid="knowledge-sources-uploading"
            >
              {t("settings.sourcesPanel.uploading")}
            </Text>
          ) : null}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function DeleteSourceDialog({
  source,
  onConfirm,
  onOpenChange,
  isDeleting,
}: {
  source: KnowledgeSourceResult | null;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
  isDeleting: boolean;
}) {
  const { t } = useTranslation("ui");
  return (
    <AlertDialog.Root open={source != null} onOpenChange={onOpenChange}>
      <AlertDialog.Content
        aria-label={t("settings.sourcesPanel.deleteSource")}
        data-testid="knowledge-sources-delete-dialog"
      >
        <AlertDialog.Title>
          {t("settings.sourcesPanel.deleteSource")}
        </AlertDialog.Title>
        <AlertDialog.Description size="2">
          Delete “{source?.file.fileName}”? Rules extracted from it keep their
          citations, but the file itself will no longer be listed.
        </AlertDialog.Description>
        <Flex gap="3" mt="4" justify="end">
          <AlertDialog.Cancel>
            <Button
              variant="soft"
              color="gray"
              data-testid="knowledge-sources-delete-cancel"
            >
              {t("common:actions.cancel")}
            </Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              color="red"
              loading={isDeleting}
              onClick={onConfirm}
              data-testid="knowledge-sources-delete-confirm"
            >
              {t("common:actions.delete")}
            </Button>
          </AlertDialog.Action>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
}

/**
 * Sources tab: upload guideline documents, watch the unified per-document
 * lifecycle chip (Processing -> Extracting rules -> Ready · N rules), jump to
 * the rules a document produced, edit scope, retry failed extractions, and
 * delete. Polling happens in useKnowledgeSourcesQuery while any source is in
 * flight.
 */
export function SourcesPanel() {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  const navigate = knowledgeDatabaseRoute.useNavigate();
  const canManage = canManageKnowledgeDatabase();

  const sourcesQuery = useKnowledgeSourcesQuery();
  const upload = useUploadKnowledgeSourceMutation();
  const updateScope = useUpdateSourceScopeMutation();
  const retryExtraction = useRetryExtractionMutation();
  const deleteSource = useDeleteSourceMutation();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleting, setDeleting] = useState<KnowledgeSourceResult | null>(null);

  const sources = sourcesQuery.data?.sources ?? [];

  function handleUpload(file: File) {
    upload.mutate(file, {
      onSuccess() {
        setUploadOpen(false);
        notify({
          title: "Upload started",
          message: `${file.name} is processing — rules will appear once extraction finishes.`,
        });
      },
      async onError(error) {
        notify({
          title: "Upload failed",
          message: await errorMessage(error),
          variant: "error",
        });
      },
    });
  }

  function goToSourceRules(sourceId: string) {
    navigate({
      search: (prev) => ({
        ...prev,
        tab: "rules",
        sourceId,
        ruleId: undefined,
      }),
    });
  }

  return (
    <section
      aria-label={t("settings.sourcesPanel.knowledgeSources")}
      data-testid="knowledge-sources-panel"
    >
      {canManage ? (
        <Flex justify="end" className="mb-3">
          <Button
            size="1"
            onClick={() => setUploadOpen(true)}
            data-testid="knowledge-sources-upload-button"
          >
            <UploadSimple aria-hidden="true" />
            Upload guidelines
          </Button>
        </Flex>
      ) : null}

      {sources.length === 0 && !sourcesQuery.isLoading ? (
        <Flex
          direction="column"
          align="center"
          gap="2"
          className="py-12"
          data-testid="knowledge-sources-empty"
        >
          <Text size="2" color="gray">
            No guideline documents yet. Upload brand guidelines, claims sheets,
            checklists, or policies — Puntt turns them into rules.
          </Text>
          {canManage ? (
            <Button
              size="1"
              onClick={() => setUploadOpen(true)}
              data-testid="knowledge-sources-empty-upload-cta"
            >
              <UploadSimple aria-hidden="true" />
              Upload your first document
            </Button>
          ) : null}
        </Flex>
      ) : (
        <Table.Root data-testid="knowledge-sources-table">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>
                {t("settings.sourcesPanel.file")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                {t("settings.sourcesPanel.trust")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                {t("settings.sourcesPanel.scope")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                {t("common:labels.status")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>
                {t("settings.sourcesPanel.rules")}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell aria-label={t("common:labels.actions")} />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sources.map((source) => {
              const status = deriveSourceStatus(source);
              return (
                <Table.Row
                  key={source._id}
                  data-testid={`knowledge-sources-row-${source._id}`}
                >
                  <Table.Cell>
                    {status.kind === "ready" && status.ruleCount > 0 ? (
                      <RadixLink
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          goToSourceRules(source._id);
                        }}
                        aria-label={`View rules extracted from ${source.file.fileName}`}
                        data-testid={`knowledge-sources-name-link-${source._id}`}
                      >
                        <Text size="2">{source.file.fileName}</Text>
                      </RadixLink>
                    ) : (
                      <Text size="2">{source.file.fileName}</Text>
                    )}
                    {source.description ? (
                      <Text as="p" size="1" color="gray">
                        {source.description}
                      </Text>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      color={
                        source.trustLevel === KnowledgeTrustLevel.HIGH
                          ? "green"
                          : "gray"
                      }
                      variant="soft"
                      data-testid={`knowledge-sources-trust-${source._id}`}
                    >
                      {source.trustLevel === KnowledgeTrustLevel.HIGH
                        ? t("settings.sourcesPanel.highTrust")
                        : t("settings.sourcesPanel.lowTrust")}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    {canManage ? (
                      <ScopeEditPopover
                        scope={source.scope}
                        scopeSource={source.scopeSource}
                        isSaving={updateScope.isPending}
                        onSave={(scope) =>
                          updateScope.mutate(
                            { sourceId: source._id, scope },
                            {
                              async onError(error) {
                                notify({
                                  title: "Could not update scope",
                                  message: await errorMessage(error),
                                  variant: "error",
                                });
                              },
                            },
                          )
                        }
                        trigger={
                          <button
                            type="button"
                            aria-label={`Edit scope for ${source.file.fileName}`}
                            className="cursor-pointer border-none bg-transparent p-0"
                            data-testid={`knowledge-sources-scope-edit-${source._id}`}
                          >
                            <ScopeChips
                              scope={source.scope}
                              testId={`knowledge-sources-scope-${source._id}`}
                            />
                          </button>
                        }
                      />
                    ) : (
                      <ScopeChips
                        scope={source.scope}
                        testId={`knowledge-sources-scope-${source._id}`}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Flex gap="2" align="center">
                      <Badge
                        color={statusColor(status)}
                        variant="soft"
                        title={
                          "error" in status && status.error
                            ? status.error
                            : undefined
                        }
                        data-testid={`knowledge-sources-status-${source._id}`}
                      >
                        {status.label}
                      </Badge>
                      {status.kind === "extraction_failed" && canManage ? (
                        <Button
                          size="1"
                          variant="soft"
                          loading={retryExtraction.isPending}
                          onClick={() =>
                            retryExtraction.mutate(source._id, {
                              async onError(error) {
                                notify({
                                  title: "Could not restart extraction",
                                  message: await errorMessage(error),
                                  variant: "error",
                                });
                              },
                            })
                          }
                          data-testid={`knowledge-sources-retry-extraction-${source._id}`}
                        >
                          {t("common:actions.retry")}
                        </Button>
                      ) : null}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    {status.kind === "ready" && status.ruleCount > 0 ? (
                      <RadixLink
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          goToSourceRules(source._id);
                        }}
                        aria-label={`View the ${status.ruleCount} rules from ${source.file.fileName}`}
                        data-testid={`knowledge-sources-rules-link-${source._id}`}
                      >
                        {status.ruleCount}{" "}
                        {status.ruleCount === 1 ? "rule" : "rules"}
                      </RadixLink>
                    ) : (
                      <Text size="1" color="gray">
                        —
                      </Text>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {canManage ? (
                      <DropdownMenu.Root>
                        <DropdownMenu.Trigger>
                          <IconButton
                            size="1"
                            variant="ghost"
                            color="gray"
                            aria-label={`Actions for ${source.file.fileName}`}
                            data-testid={`knowledge-sources-actions-${source._id}`}
                          >
                            <DotsThreeOutlineVertical aria-hidden="true" />
                          </IconButton>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content>
                          <DropdownMenu.Item
                            color="red"
                            onClick={() => setDeleting(source)}
                            data-testid={`knowledge-sources-delete-${source._id}`}
                          >
                            {t("common:actions.delete")}
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Root>
                    ) : null}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      )}

      <UploadSourceDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUpload={handleUpload}
        isUploading={upload.isPending}
      />
      <DeleteSourceDialog
        source={deleting}
        isDeleting={deleteSource.isPending}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onConfirm={() => {
          if (!deleting) return;
          deleteSource.mutate(deleting._id, {
            onSuccess() {
              setDeleting(null);
            },
            async onError(error) {
              notify({
                title: "Could not delete source",
                message: await errorMessage(error),
                variant: "error",
              });
            },
          });
        }}
      />
    </section>
  );
}
